import { CANVAS_TOOLS } from "../nativeCanvasElements";
import { resolveSceneArrowBindings } from "../geometry/arrowBinding";
import {
  getStageCenterScreenPoint,
  stepViewportZoom,
} from "../geometry/viewportTransform";
import { createCanvasHistory } from "./canvasHistory";
import { createImageActions } from "./imageActions";
import { createPointerHandlers } from "./pointerSession";
import { createSelectionActions } from "./selectionActions";
import { createTextEditingActions } from "./textEditingActions";
import { getSelectionStyle } from "./sceneOperations";

const TOOL_CURSORS = Object.freeze({
  [CANVAS_TOOLS.HAND]: "grab",
  [CANVAS_TOOLS.SELECT]: "default",
});

const areIdSetsEqual = (first, second) =>
  first.size === second.size && [...first].every((id) => second.has(id));

const cloneCanvasElement = (element) => ({
  ...element,
  bindings: element.bindings
    ? {
        end: element.bindings.end ? { ...element.bindings.end } : undefined,
        start: element.bindings.start ? { ...element.bindings.start } : undefined,
      }
    : undefined,
  points: Array.isArray(element.points)
    ? element.points.map((point) => ({ ...point }))
    : element.points,
  style: element.style ? { ...element.style } : element.style,
});

/**
 * Imperative canvas store. The scene lives outside React; React subscribes
 * only to a small UI snapshot (tool, selection, zoom, undo state) while all
 * per-frame work goes through the rAF render scheduler. `defaultTool` is the
 * tool the canvas starts with and reverts to after creating elements (hand on
 * touch-primary devices, selection otherwise).
 * @param {{ defaultTool?: string, snapshot: { elements: Array<object>, viewport: { x: number, y: number, zoom: number } } }} params
 * @returns {object} canvas engine
 */
export const createCanvasEngine = ({
  defaultTool = CANVAS_TOOLS.SELECT,
  snapshot,
}) => {
  const state = {
    bindTargetId: null,
    draftElement: null,
    editingText: null,
    elements: resolveSceneArrowBindings(snapshot.elements),
    isSpacePanning: false,
    isToolLocked: false,
    lastPointerScenePoint: null,
    liftedElements: null,
    marqueeBox: null,
    selectedIds: new Set(),
    session: null,
    size: { height: 0, width: 0 },
    tool: defaultTool,
    viewport: { ...snapshot.viewport },
  };
  const history = createCanvasHistory(state.elements);
  const uiListeners = new Set();
  const sceneListeners = new Set();
  let elementRevision = 0;
  let cachedUiSnapshot = null;
  let renderRequester = () => {};
  let cursorSink = () => {};

  const requestRender = (layer) => renderRequester(layer);
  const notifyScene = () => sceneListeners.forEach((listener) => listener());
  const notifyUi = () => {
    cachedUiSnapshot = null;
    uiListeners.forEach((listener) => listener());
  };

  const setCursor = (cursor) => cursorSink(cursor);
  const markElementRevision = () => {
    elementRevision += 1;
  };
  const getBaseCursor = () =>
    state.isSpacePanning ? "grab" : TOOL_CURSORS[state.tool] ?? "crosshair";

  const setSelectedIds = (nextIds) => {
    if (areIdSetsEqual(state.selectedIds, nextIds)) return;
    state.selectedIds = nextIds;
    notifyUi();
    requestRender("overlay");
  };

  const commitElements = (nextElements, { selectIds } = {}) => {
    const resolvedElements = resolveSceneArrowBindings(nextElements);
    history.push(resolvedElements);
    state.elements = resolvedElements;
    if (selectIds) state.selectedIds = selectIds;
    markElementRevision();
    notifyUi();
    notifyScene();
    requestRender("both");
  };

  const updateViewport = (nextViewport) => {
    const hasZoomLabelChanged =
      Math.round(nextViewport.zoom * 100) !== Math.round(state.viewport.zoom * 100);
    state.viewport = nextViewport;
    if (hasZoomLabelChanged) notifyUi();
    notifyScene();
    requestRender("both");
  };

  const textEditing = createTextEditingActions({
    commitElements,
    notifyUi,
    requestRender,
    state,
  });

  const setTool = (tool) => {
    textEditing.commitTextEditing();
    if (state.tool === tool) return;
    state.tool = tool;
    notifyUi();
    setCursor(getBaseCursor());
    requestRender("overlay");
  };

  const handleToolUsed = () => {
    if (state.isToolLocked || state.tool === defaultTool) return;
    setTool(defaultTool);
  };

  const pointer = createPointerHandlers({
    internals: {
      commitElements,
      commitTextEditing: textEditing.commitTextEditing,
      getBaseCursor,
      handleToolUsed,
      insertTextElementAt: textEditing.insertTextElementAt,
      requestRender,
      setCursor,
      setSelectedIds,
      startTextEditing: textEditing.startTextEditing,
      updateViewport,
    },
    state,
  });

  const restoreHistoryElements = (nextElements) => {
    if (!nextElements) return;
    pointer.cancelSession();
    state.editingText = null;
    state.elements = nextElements;
    markElementRevision();
    state.selectedIds = new Set(
      [...state.selectedIds].filter((id) =>
        nextElements.some((element) => element.id === id)
      )
    );
    notifyUi();
    notifyScene();
    requestRender("both");
  };

  const buildUiSnapshot = () => ({
    canRedo: history.canRedo(),
    canUndo: history.canUndo(),
    editingElementId: state.editingText?.elementId ?? null,
    editingValue: state.editingText?.value ?? "",
    isToolLocked: state.isToolLocked,
    selectedCount: state.selectedIds.size,
    selectedStyle: getSelectionStyle({
      elements: state.elements,
      ids: state.selectedIds,
    }),
    tool: state.tool,
    zoomPercent: Math.round(state.viewport.zoom * 100),
  });

  const imageActions = createImageActions({
    commitElements,
    defaultTool,
    markElementRevision,
    notifyScene,
    requestRender,
    setTool,
    state,
  });

  const selectionActions = createSelectionActions({
    commitElements,
    defaultTool,
    setSelectedIds,
    setTool,
    state,
  });

  /**
   * Cancels the most immediate in-progress interaction: text editing, then an
   * active pointer session/draft, then a non-empty selection.
   * @returns {boolean} true if it canceled something, false if already idle
   */
  const cancelInteraction = () => {
    if (state.editingText) {
      textEditing.cancelTextEditing();
      return true;
    }
    if (pointer.hasActiveSession() || state.draftElement) {
      pointer.cancelSession();
      return true;
    }
    if (!state.selectedIds.size) return false;
    setSelectedIds(new Set());
    return true;
  };

  const getSceneCheckpoint = () => ({
    elements: state.elements.map(cloneCanvasElement),
    revision: elementRevision,
    size: { ...state.size },
    viewport: { ...state.viewport },
  });

  const hasActiveElementInteraction = () =>
    Boolean(state.editingText || state.draftElement || pointer.hasActiveSession());

  const commitSceneTransaction = ({ baseRevision, elements, selectedIds }) => {
    if (baseRevision !== elementRevision || hasActiveElementInteraction()) {
      return { committed: false, reason: "conflict" };
    }
    const existingIds = new Set(elements.map((element) => element.id));
    const safeSelectedIds = new Set(
      [...(selectedIds ?? [])].filter((elementId) => existingIds.has(elementId))
    );
    commitElements(elements, { selectIds: safeSelectedIds });
    return { committed: true, revision: elementRevision };
  };

  const undoSceneTransaction = (revision) => {
    if (revision !== elementRevision) return false;
    const previousElements = history.undo();
    if (!previousElements) return false;
    restoreHistoryElements(previousElements);
    return true;
  };

  return {
    addImage: imageActions.addImage,
    applyStyle: selectionActions.applyStyle,
    cancelInteraction,
    cancelTextEditing: textEditing.cancelTextEditing,
    commitTextEditing: textEditing.commitTextEditing,
    commitSceneTransaction,
    deleteSelected: selectionActions.deleteSelected,
    duplicateSelected: selectionActions.duplicateSelected,
    getBaseCursor,
    getEditingElement: textEditing.getEditingElement,
    getElementRevision: () => elementRevision,
    getSceneCheckpoint,
    getSelectedElements: selectionActions.getSelectedElements,
    getState: () => state,
    getUiSnapshot: () => {
      if (!cachedUiSnapshot) cachedUiSnapshot = buildUiSnapshot();
      return cachedUiSnapshot;
    },
    loadSnapshot: (nextSnapshot) => {
      pointer.cancelSession();
      state.elements = resolveSceneArrowBindings(nextSnapshot.elements);
      state.viewport = { ...nextSnapshot.viewport };
      state.selectedIds = new Set();
      state.editingText = null;
      history.reset(state.elements);
      markElementRevision();
      notifyUi();
      requestRender("both");
    },
    nudgeSelected: selectionActions.nudgeSelected,
    pasteElements: selectionActions.pasteElements,
    pointer,
    redo: () => restoreHistoryElements(history.redo()),
    replaceImageSource: imageActions.replaceImageSource,
    selectAll: selectionActions.selectAll,
    serializeSnapshot: () => ({
      elements: state.elements,
      viewport: state.viewport,
    }),
    setCursorSink: (sink) => {
      cursorSink = sink ?? (() => {});
      cursorSink(getBaseCursor());
    },
    setRenderRequester: (requester) => {
      renderRequester = requester ?? (() => {});
    },
    setSize: (size) => {
      state.size = size;
    },
    setSpacePanning: (isSpacePanning) => {
      if (state.isSpacePanning === isSpacePanning) return;
      state.isSpacePanning = isSpacePanning;
      setCursor(getBaseCursor());
    },
    setTool,
    subscribeScene: (listener) => {
      sceneListeners.add(listener);
      return () => sceneListeners.delete(listener);
    },
    subscribeUi: (listener) => {
      uiListeners.add(listener);
      return () => uiListeners.delete(listener);
    },
    toggleToolLock: () => {
      state.isToolLocked = !state.isToolLocked;
      notifyUi();
    },
    undo: () => restoreHistoryElements(history.undo()),
    undoSceneTransaction,
    updateEditingValue: textEditing.updateEditingValue,
    updateViewport,
    zoomBy: ({ direction, screenPoint }) => {
      updateViewport(
        stepViewportZoom({
          direction,
          screenPoint: screenPoint ?? getStageCenterScreenPoint(state.size),
          viewport: state.viewport,
        })
      );
    },
  };
};
